import { createSelector } from 'reselect';
import { friendsPlainSelector, friendsSelector } from '../radarFriends';
import { invitationsPlainSelector } from '../radarInvitations';
import { convertToMap } from '../../utils';
import { radarConnectionsSelector } from './slice';
import { LIST_ROW_STATUS } from '../../utils/cometChat';

// lists are ordered from oldest to newest - after merge they need to be reversed
export const connectionsSelector = createSelector(
  friendsPlainSelector,
  invitationsPlainSelector,
  (
    { loading: loadingFriends, refreshing: refreshingFriends, items: friends },
    {
      loading: loadingInvitations,
      refreshing: refreshingInvitations,
      items: invitations,
    },
  ) => {
    // list of invitations sent to me
    const head = [];

    // list of friends and other invitations
    const rest = [];

    const friendsMapped = friends.map(({ status, ...friend }) => ({
      ...friend,
      status: status?.toLowerCase(),
    }));

    const invitationsMapped = invitations
      .map(({ user, invitee, ...invitation } = {}) => {
        if (!invitation) {
          return null;
        }
        let userObject;
        let status;
        if (user?.nickname) {
          userObject = user;
          status = LIST_ROW_STATUS.PENDING;
        } else if (invitee?.nickname) {
          userObject = invitee;
          status = LIST_ROW_STATUS.WAITING;
        } else {
          return null;
        }
        return {
          ...userObject,
          ...invitation,
          status,
        };
      })
      .filter(Boolean);

    const listsLength = friendsMapped.length + invitationsMapped.length;
    let fIdx = 0;
    let iIdx = 0;

    while (fIdx + iIdx < listsLength) {
      const invitation = invitationsMapped[iIdx];
      const friend = friendsMapped[fIdx];

      if (invitation?.user?.cuid && invitation?.status === 'awaiting') {
        // invitation sent to me
        head.push(invitation);
        iIdx += 1;
      } else if (
        invitation &&
        (!friend || invitation?.createdAt < friend.createdAt)
      ) {
        rest.push(invitation);
        iIdx += 1;
      } else if (
        friend &&
        (!invitation || friend?.createdAt < invitation?.createdAt)
      ) {
        rest.push(friend);
        fIdx += 1;
      }
    }

    return {
      loading: loadingFriends || loadingInvitations,
      refreshing: refreshingFriends || refreshingInvitations,
      items: head.reverse().concat(rest.reverse()),
    };
  },
);

export const connectionsListSelector = createSelector(
  connectionsSelector,
  radarConnectionsSelector,
  (connections, { search, searchedFriend }) => {
    let { items } = connections;
    if (search) {
      const filter = search.toLowerCase();
      items = items.filter(
        ({ nickname }) => nickname && nickname.toLowerCase().includes(filter),
      );
    }

    if (searchedFriend) {
      items = items.filter(({ cuid }) => cuid !== searchedFriend.cuid); // if nickname would be the same as code
      const duplicate = connections.items.find(
        ({ cuid }) => cuid === searchedFriend.cuid,
      );
      if (!duplicate) {
        items = [
          { ...searchedFriend, uid: search, status: LIST_ROW_STATUS.NEW },
          ...items,
        ];
      } else {
        items = [duplicate, ...items];
      }
    }

    return {
      ...connections,
      items,
      search,
      searchedFriend,
    };
  },
);

export const connectionsMappedSelector = createSelector(
  friendsSelector,
  (friends) => convertToMap(friends, {}, 'cuid'),
);
